import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, ImageIcon, Eye, EyeOff, X } from "lucide-react";
import { marked } from "marked";

const CATEGORIES = [
  "Website Security", "SSL Certificates", "Cybersecurity",
  "Website Design", "Hosting", "Domains", "Technical Guides",
];

marked.setOptions({ gfm: true, breaks: true });

export default function AdminBlog() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/blog/posts").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/admin/blog/posts/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Blog</h1>
          <p className="text-sm text-muted-foreground mt-1">Publish articles that grow your SEO footprint.</p>
        </div>
        <button onClick={() => setEditing({ id: null, title: "", slug: "", excerpt: "", content: "", category: "Technical Guides", tags: [], featured_image: null, author: "Tech Alpha SSL", draft: false, meta_title: "", meta_description: "" })}
          className="btn-primary !py-2 !px-4 !text-sm" data-testid="admin-blog-new"><Plus className="h-4 w-4"/> New post</button>
      </div>

      {editing && <PostEditor initial={editing} onClose={() => setEditing(null)} onSaved={load} />}

      <div className="mt-6 card-elevated overflow-hidden" data-testid="admin-blog-list">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-black/[0.02] dark:bg-white/[0.02] text-left">
            <tr><th className="px-4 py-3">Title</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Views</th><th className="px-4 py-3">Updated</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t border-black/5 dark:border-white/10">
                <td className="px-4 py-3"><span className="font-medium">{r.title}</span><div className="text-xs text-muted-foreground">/blog/{r.slug}</div></td>
                <td className="px-4 py-3"><span className="text-xs rounded-full bg-[#1565FF]/10 text-[#1565FF] px-2 py-0.5">{r.category}</span></td>
                <td className="px-4 py-3">{r.draft
                  ? <span className="text-xs rounded-full bg-amber-500/10 text-amber-600 px-2 py-0.5">Draft</span>
                  : <span className="text-xs rounded-full bg-emerald-500/10 text-emerald-600 px-2 py-0.5">Published</span>}
                </td>
                <td className="px-4 py-3">{r.view_count || 0}</td>
                <td className="px-4 py-3 text-muted-foreground text-xs">{new Date(r.updated_at || r.created_at).toLocaleString()}</td>
                <td className="px-4 py-3 flex gap-2">
                  <button onClick={() => setEditing(r)} className="text-sm text-[#1565FF]" data-testid={`admin-blog-edit-${r.id}`}>Edit</button>
                  <button onClick={() => del(r.id)} className="text-destructive" data-testid={`admin-blog-delete-${r.id}`}><Trash2 className="h-4 w-4"/></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (<tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No posts yet.</td></tr>)}
          </tbody>
        </table>
        </div>
      </div>
    </>
  );
}

function PostEditor({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ ...initial, tags: (initial.tags || []).join(", ") });
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(false);
  const fileRef = useRef(null);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) return toast.error("Please upload an image under 1MB");
    const reader = new FileReader();
    reader.onload = () => setF({ ...f, featured_image: reader.result });
    reader.readAsDataURL(file);
  };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        title: f.title,
        slug: f.slug || null,
        excerpt: f.excerpt || null,
        content: f.content,
        category: f.category,
        tags: (f.tags || "").split(",").map(t => t.trim()).filter(Boolean),
        featured_image: f.featured_image || null,
        author: f.author || "Tech Alpha SSL",
        draft: !!f.draft,
        meta_title: f.meta_title || null,
        meta_description: f.meta_description || null,
      };
      if (f.id) await api.put(`/admin/blog/posts/${f.id}`, payload);
      else await api.post("/admin/blog/posts", payload);
      toast.success("Saved");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm overflow-y-auto p-4" data-testid="admin-blog-editor">
      <form onSubmit={save} className="max-w-4xl mx-auto my-8 rounded-2xl bg-white dark:bg-[#0d1a35] border border-black/5 dark:border-white/10 shadow-2xl overflow-hidden">
        <div className="p-6 border-b border-black/5 dark:border-white/10 flex items-center justify-between">
          <h3 className="text-lg font-semibold tracking-tight">{f.id ? "Edit post" : "New post"}</h3>
          <div className="flex gap-2">
            <button type="button" onClick={() => setPreview(v => !v)} className="btn-ghost !py-1.5 !px-3 !text-xs" data-testid="admin-blog-preview">
              {preview ? <><EyeOff className="h-3.5 w-3.5"/> Edit</> : <><Eye className="h-3.5 w-3.5"/> Preview</>}
            </button>
            <button type="button" onClick={onClose} className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/5"><X className="h-4 w-4"/></button>
          </div>
        </div>

        <div className="p-6 space-y-3">
          <input required placeholder="Title" value={f.title} onChange={(e) => setF({...f, title: e.target.value})} data-testid="admin-blog-title"
            className="w-full text-2xl font-semibold tracking-tight rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2" />
          <input placeholder="Slug (auto-generated if empty)" value={f.slug || ""} onChange={(e) => setF({...f, slug: e.target.value})} data-testid="admin-blog-slug"
            className="w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" />
          <input placeholder="Short excerpt" value={f.excerpt || ""} onChange={(e) => setF({...f, excerpt: e.target.value})} data-testid="admin-blog-excerpt"
            className="w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" />

          <div className="grid gap-3 md:grid-cols-3">
            <select value={f.category} onChange={(e) => setF({...f, category: e.target.value})} data-testid="admin-blog-category"
              className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm">
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input placeholder="Author" value={f.author || ""} onChange={(e) => setF({...f, author: e.target.value})} data-testid="admin-blog-author"
              className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" />
            <input placeholder="Tags (comma-separated)" value={f.tags} onChange={(e) => setF({...f, tags: e.target.value})} data-testid="admin-blog-tags"
              className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" />
          </div>

          <div className="flex items-center gap-4">
            {f.featured_image ? (
              <img src={f.featured_image} alt="preview" className="h-20 w-32 object-cover rounded-lg border border-black/10 dark:border-white/10" />
            ) : (
              <div className="h-20 w-32 rounded-lg bg-black/5 dark:bg-white/5 grid place-items-center text-muted-foreground"><ImageIcon className="h-5 w-5"/></div>
            )}
            <div>
              <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="admin-blog-image-file" />
              <button type="button" onClick={() => fileRef.current?.click()} className="btn-ghost !py-1.5 !px-3 !text-xs">Upload featured image (max 1MB)</button>
              {f.featured_image && <button type="button" onClick={() => setF({...f, featured_image: null})} className="ml-2 text-xs text-destructive">Remove</button>}
            </div>
          </div>

          {!preview ? (
            <textarea required rows={16} placeholder="Write your post in **Markdown**…" value={f.content} onChange={(e) => setF({...f, content: e.target.value})} data-testid="admin-blog-content"
              className="w-full font-mono rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" />
          ) : (
            <div className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-6 py-4 prose prose-neutral dark:prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: marked.parse(f.content || "") }} />
          )}

          <div className="pt-3 border-t border-black/5 dark:border-white/10 space-y-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">SEO</div>
            <input placeholder="Meta title (SEO)" value={f.meta_title || ""} onChange={(e) => setF({...f, meta_title: e.target.value})} data-testid="admin-blog-meta-title"
              className="w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" />
            <textarea placeholder="Meta description (SEO)" rows={2} value={f.meta_description || ""} onChange={(e) => setF({...f, meta_description: e.target.value})} data-testid="admin-blog-meta-desc"
              className="w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" />
          </div>
        </div>

        <div className="p-6 border-t border-black/5 dark:border-white/10 flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.draft} onChange={(e) => setF({...f, draft: e.target.checked})} data-testid="admin-blog-draft" /> Save as draft
          </label>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-ghost !py-2 !px-4 !text-sm">Cancel</button>
            <button disabled={loading} className="btn-primary !py-2 !px-4 !text-sm" data-testid="admin-blog-save"><Save className="h-4 w-4"/> Save post</button>
          </div>
        </div>
      </form>
    </div>
  );
}
